// 🔧 auction.js
// ✅ Purpose: Public-facing auction listing page
// ✅ Features:
// - Shows all open auction items for the current event
// - Respects auctionEnabled setting
// - Displays current bid
// - Displays bid count
// - Displays auction status
// - Links to individual auction item bidding page
// - Supports future search/filtering
// - Fully block-commented for clarity and future devs
// - Published future items show countdowns; finished items disable bidding
// - Own finalized orders link to payment details; next bids require confirmation

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { showStatus } from "../../utils/status_helpers";
import AuctionBidderRegistration from "../../components/auction_bidder_registration";
import { useAuctionRegistration } from "../../hooks/use_auction_registration";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import {
  AUCTION_STATUS,
  getAuctionStatus,
  getAuctionStatusLabel,
  isPublicAuctionItem,
  getMinimumAuctionBidCents,
  getAuctionCountdown,
} from "../../utils/auction_status";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
const Auction = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const auctionEnabled = hasPurchasedAddOn(
    eventDoc,
    "auction"
  );
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [notification, setNotification] = useState(null);
  const [itemsError, setItemsError] = useState("");
  const [winnerState, setWinnerState] = useState(null);
  const [biddingItemId, setBiddingItemId] = useState("");
  const [favoriteIds, setFavoriteIds] = useState(() => new Set(JSON.parse(localStorage.getItem(`lgv_auction_favorites_${eventId || ""}`) || "[]")));
  const bidInFlight = useRef(false);
  const registration = useAuctionRegistration({
    eventId, required: eventDoc?.auctionRegistrationRequired === true, user,
    onMessage: (type, message) => showStatus(setNotification, { type, message }),
  });

  useEffect(() => {
    if (ready && eventId) trackEvent({ action: GA_EVENTS.AUCTION_LIST_VIEWED, parameters: { event_slug: eventslug, event_id: eventId, source_page: window.location.pathname } });
  }, [ready, eventId, eventslug]);

  useEffect(() => {
    if (!ready || !eventId || !user?.uid || !auctionEnabled || !hasPurchasedAddOn(eventDoc, "passport")) return;
    httpsCallable(functions, "completeAuctionViewActivity")({ eventId })
      .catch((error) => logger.warn("Unable to complete Auction Passport activity", { value: error }));
  }, [ready, eventId, user?.uid, auctionEnabled, eventDoc]);

  // Read only this buyer's finalized orders, never other bidders' allocations.
  useEffect(() => {
    if (!ready || !eventId || !user?.uid || !auctionEnabled) return;
    const owner = `${eventId}:${user.uid}`;
    setWinnerState({ owner, orders: [], loading: true, error: "" });
    return onSnapshot(query(
      collection(db, "events", eventId, "orders"),
      where("uid", "==", user.uid),
      where("orderType", "==", "auction")
    ), (snapshot) => {
      setWinnerState({
        owner,
        orders: snapshot.docs.map((record) => ({ ...record.data(), id: record.id })),
        loading: false,
        error: "",
      });
    }, (err) => {
      logger.error("Failed to load auction winnings", { value: err });
      setWinnerState({ owner, orders: [], loading: false, error: "Unable to load your auction winnings." });
    });
  }, [ready, eventId, user?.uid, auctionEnabled]);

  // Scope the displayed state too: an auth/event change must hide old orders
  // immediately, before the replacement subscription effect runs.
  const myWinnings = user?.uid && winnerState?.owner === `${eventId}:${user.uid}`
    ? winnerState : null;

  useEffect(() => {
    if (!ready || !eventId) return;

    const unsubscribe = loadItems();

    return () => unsubscribe?.();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventId]);

  const loadItems = () => {
    try {
      setLoadingItems(true);
      setItemsError("");

      const auctionRef = collection(
        db,
        "events",
        eventId,
        "auctionItems"
      );

      const auctionQuery = query(
        auctionRef,
        where("published", "==", true)
      );

      return onSnapshot(auctionQuery, (snap) => {

    const loadedItems = snap.docs
    .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
    }))
    .filter((item) => item.placeholder !== true)
    .sort((left, right) => String(left.title || "").localeCompare(String(right.title || "")));

      logger.log("✅ Auction items loaded", {
        value: loadedItems.length,
      });

      setItems(loadedItems);
      setLoadingItems(false);
      }, (err) => {
      logger.error("❌ Failed loading auction items", {
        value: err,
      });
      setLoadingItems(false);
      setItemsError("Unable to load auction items. Please refresh and try again.");
      });
    } catch (err) {
      logger.error("Unable to start live auction items", { value: err });
      setLoadingItems(false);
      setItemsError("Unable to load auction items. Please refresh and try again.");
      return undefined;
    }
  };

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getPublicState = (item) => getAuctionStatus({ item, event: eventDoc, now: currentTime });

  const submitQuickBid = async (item) => {
    if (bidInFlight.current) return;
    if (!user) return showStatus(setNotification, { type: "warning", message: "Log in before placing a bid." });
    if (getAuctionStatus({ item, event: eventDoc, now: new Date() }) !== AUCTION_STATUS.OPEN_FOR_BIDDING) return;
    if (eventDoc?.auctionRegistrationRequired === true && !(await registration.checkBeforeBid())) return;
    // Registration lookup can cross the closing instant; check again before confirming.
    if (getAuctionStatus({ item, event: eventDoc, now: new Date() }) !== AUCTION_STATUS.OPEN_FOR_BIDDING) return;
    const bidAmountCents = getMinimumAuctionBidCents(item);
    const amount = bidAmountCents / 100;
    if (!window.confirm(`Place a bid of ${formatMoney(amount)} for ${item.title || "this item"}?`)) return;
    bidInFlight.current = true;
    setBiddingItemId(item.id);
    try {
      await httpsCallable(functions, "placeAuctionBid")({ eventId, auctionItemId: item.id, bidAmountCents });
      showStatus(setNotification, { type: "success", message: `Your bid of ${formatMoney(amount)} was recorded.` });
    } catch (err) {
      if (["functions/failed-precondition", "functions/permission-denied", "functions/unauthenticated", "functions/invalid-argument"].includes(err?.code)) {
        console.warn("Auction bid was not accepted", { code: err.code });
      } else {
        logger.error("Failed to place quick auction bid", { value: err });
      }
      showStatus(setNotification, { type: "error", message: err?.message || "Unable to place bid." });
    } finally {
      bidInFlight.current = false;
      setBiddingItemId("");
    }
  };

  const publicItems = items.filter((item) =>
    item.published === true && isPublicAuctionItem(getPublicState(item))
  );

  const normalizedSearch = searchTerm
    .trim()
    .toLowerCase();

  const filteredItems = publicItems.filter((item) =>
    [
      item.title,
      item.description,
      item.linkedEntryLabel,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedSearch)
  ).sort((left, right) => Number(favoriteIds.has(right.id)) - Number(favoriteIds.has(left.id)));
  const toggleFavorite = (itemId) => setFavoriteIds((current) => {
    const next = new Set(current);
    next.has(itemId) ? next.delete(itemId) : next.add(itemId);
    localStorage.setItem(`lgv_auction_favorites_${eventId || ""}`, JSON.stringify([...next]));
    return next;
  });

    // =====================================================
  // 🔎 Dynamic SEO
  // =====================================================

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Auction",
      path: `/${eventslug}/auction`,
    },
  ];

  const approvedAuctionSeo = buildEventPageSeo({
    eventDoc,
    pageKey: "auction",

    fallbackTitle: `${eventDoc?.name || "Event"} Silent Auction | Let's Go Vote`,

    fallbackDescription:
      `Browse silent auction items for ${
        eventDoc?.name || "this event"
      }.`,
  });

  const auctionSeo = {
    ...approvedAuctionSeo,

    path: `/${eventslug}/auction`,

    breadcrumbs,
  };

  const auctionEnd = eventDoc?.auctionEnd;
  const auctionIsEnding = auctionEnd && new Date(auctionEnd?.toDate?.() || auctionEnd).getTime() > currentTime.getTime();

  if (!ready || eventLoading) {
    return (
      <FullPageSpinner message="Loading Auction..." />
    );
  }

  if (!auctionEnabled) {
    return (
      <>
        <SeoHead {...auctionSeo} />

        <section
          id="top-content"
          className="container-fluid py-5"
        >
          <div className="container">
            <div className="alert alert-warning">
              Silent Auction is not enabled for this event.
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead {...auctionSeo} />

      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "1200px" }}>
          <div className="text-center mb-4">
            <h1 className="display-5 fw-bold mb-2">
              🏷️ Silent Auction
            </h1>

            <p className="lead text-muted mb-0">
              Browse auction items and place bids for this event.
            </p>
          </div>

          {notification && <div role="alert" className={`alert alert-${notification.type === "error" ? "danger" : notification.type}`}>{notification.message}</div>}
          <AuctionBidderRegistration {...registration.panelProps} />
          {itemsError && <div role="alert" className="alert alert-danger">{itemsError}</div>}
          {myWinnings?.loading && <div role="status" className="alert alert-info">Loading your auction winnings...</div>}
          {myWinnings?.error && <div role="alert" className="alert alert-danger">{myWinnings.error}</div>}
          {!!myWinnings?.orders.length && (
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h4">Your Won Items</h2>
                <ul className="list-group list-group-flush">
                  {myWinnings.orders.map((order) => (
                    <li key={order.id} className="list-group-item d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <span>{order.auctionItemTitle || "Auction item"} — {formatMoney(Number(order.amountCents ?? order.totalAmount ?? 0) / 100)}</span>
                      {order.status === "paid" && order.fulfillmentStatus === "ready_for_pickup" && <span className="badge text-bg-success">Paid — ready for pickup</span>}
                      {order.demoWinnerEmailStatus === "simulated_sent" && <span className="badge text-bg-info">Demo email sent: {order.demoWinnerEmailReason}</span>}
                      <Link className="btn btn-outline-primary" to={`/${eventslug}/my_orders?type=auction#order-${order.id}`}>
                        {order.status === "paid" ? "View Order" : "Payment Details"}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {auctionIsEnding && <div className="alert alert-primary text-center" role="timer" aria-label="Auction ends in">
            <strong>Auction Ends In: </strong>{getAuctionCountdown(auctionEnd, currentTime)}
          </div>}

          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <strong>Auction Tools</strong>
            </div>

            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-8">
                  <label className="form-label">Search Auction Items</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by title, description, or linked entry..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchTerm.trim()) trackEvent({ action: GA_EVENTS.AUCTION_SEARCH_SUBMITTED, parameters: { event_slug: eventslug, event_id: eventId, source_page: window.location.pathname } });
                    }}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <div className="text-md-end text-muted">
                    Showing <strong>{filteredItems.length}</strong> of{" "}
                    <strong>{publicItems.length}</strong> item
                    {publicItems.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loadingItems ? (
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="alert alert-info mb-0">
                  Loading auction items...
                </div>
              </div>
            </div>
          ) : publicItems.length === 0 ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <h4>No Auction Items Available</h4>
                <p className="text-muted mb-0">
                  Auction items have not been added yet.
                </p>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <h4>No Auction Items Found</h4>
                <p className="text-muted mb-0">
                  No auction items match your current search.
                </p>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {filteredItems.map((item) => {
                const publicState = getPublicState(item);
                const nextBidCents = getMinimumAuctionBidCents(item);

                return (
                  <div key={item.id} className="col-12 col-md-6 col-xl-4">
                    <div className="card h-100 shadow-sm">
                      <div
                        className="bg-light border-bottom d-flex align-items-center justify-content-center"
                        style={{
                          minHeight: "190px",
                          overflow: "hidden",
                        }}
                      >
                        {item.imageUrls?.[0] || item.imageUrl ? (
                          <Link to={`/${eventslug}/auction/${item.auctionSlug || item.id}`} className="w-100">
                          <img
                            src={item.imageUrls?.[0] || item.imageUrl}
                            alt={item.title || "Auction item"}
                            className="img-fluid"
                            style={{
                              width: "100%",
                              height: "190px",
                              objectFit: "cover",
                            }}
                          />
                          </Link>
                        ) : (
                          <div className="text-muted">
                            No Image
                          </div>
                        )}
                      </div>

                      <div className="card-body d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                          <h5 className="mb-0"><Link to={`/${eventslug}/auction/${item.auctionSlug || item.id}`}>{item.title || "Untitled Item"}</Link></h5>
                          <div className="d-flex align-items-center gap-2">
                          <button type="button" className={`btn btn-sm ${favoriteIds.has(item.id) ? "btn-warning" : "btn-outline-secondary"}`} aria-label={`${favoriteIds.has(item.id) ? "Remove" : "Add"} ${item.title || "item"} ${favoriteIds.has(item.id) ? "from" : "to"} favorites`} onClick={() => toggleFavorite(item.id)}>{favoriteIds.has(item.id) ? "★" : "☆"}</button>
                          <span
                            className={`badge ${
                              publicState === AUCTION_STATUS.OPEN_FOR_BIDDING
                                ? "bg-success"
                                : publicState === AUCTION_STATUS.STARTING_SOON
                                ? "bg-warning text-dark"
                                : "bg-secondary"
                            }`}
                          >
                            {getAuctionStatusLabel(publicState)}
                          </span>
                          </div>
                        </div>

                        <div className="small text-muted mb-3">
                          {item.linkedEntryLabel || "Auction Item"}
                        </div>

                        {Number(item.quantityAvailable || 1) > 1 && (
                          <div className="small text-primary mb-2">
                            {item.quantityAvailable} identical items available. Top unique bidders win one each.
                          </div>
                        )}

                        {item.description && (
                          <p className="text-muted small mb-3">
                            {String(item.description).length > 140
                              ? `${String(item.description).slice(0, 140)}...`
                              : item.description}
                          </p>
                        )}

                        {publicState === AUCTION_STATUS.STARTING_SOON ? (
                          <div className="border rounded p-3 mt-auto text-center" role="timer" aria-label={`Auction starts in for ${item.title}`}>
                            <div className="small text-muted">Auction Starts In</div>
                            <div className="fs-4 fw-bold">{getAuctionCountdown(eventDoc?.auctionStart || item.auctionStart, currentTime)}</div>
                          </div>
                        ) : publicState === AUCTION_STATUS.FINISHED ? (
                          <div className="alert alert-secondary mt-auto mb-0">Auction Finished</div>
                        ) : (
                        <div className="row g-3 mt-auto mb-3">
                          <div className="col-6">
                            <div className="border rounded p-2 h-100">
                              <div className="small text-muted">
                                Next Bid
                              </div>

                              <div className="fw-bold">
                                {formatMoney(nextBidCents / 100)}
                              </div>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="border rounded p-2 h-100">
                              <div className="small text-muted">
                                Place Bid
                              </div>

                              <button className="btn btn-primary w-100" disabled={!!biddingItemId || registration.checking} onClick={() => submitQuickBid(item)} aria-label={`Bid ${formatMoney(nextBidCents / 100)} on ${item.title || "this item"}`}>
                                {registration.checking ? "Checking registration..." : biddingItemId === item.id ? "Submitting..." : `Bid ${formatMoney(nextBidCents / 100)}`}
                              </button>
                            </div>
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );

};
export default Auction;
